import React from 'react';

import cn from 'classnames';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getPlainTextFromHtml, handleStatusError } from '@shared/helpers';
import { useHandleForm } from '@shared/hooks';
import { CustomModal, Flex, UiButton, UiText, TextEditor, Image } from '@shared/ui';
import { useGetPollEnable } from '@widgets/UserPoll/api/hooks';
import { useUserPoll } from '@widgets/UserPoll/lib';

import cls from './UserPoll.module.css';

export const UserPoll = () => {
    const { t } = useTranslation();
    const { data, isError, error } = useGetPollEnable();
    const {
        skipBtn,
        // userText,
        isAnswered,
        isMutating,
        isFinished,
        // onChangeUserText,
        handlePollAnswer,
        handleSkip,
    } = useUserPoll();

    const { handleSubmit, control, isValid } = useHandleForm<{ userText: string }>({
        onSubmit: async (formData) => {
            await handlePollAnswer(formData.userText);
        },
        defaultValues: {
            userText: '',
        },
        resetEnable: false,
        mode: 'onChange',
    });

    if (isError) {
        handleStatusError(error, t('button.got_it.text'));
    }

    const isModalOpen = data && data?.data?.need === true && !isFinished;

    return (
        <CustomModal
            isOpen={isModalOpen}
            onHideModal={() => false}
            closeButtonClass={cls.closeBtn}
            contentClasses={cls.ModalContent}
        >
            <CustomModal.Header classes={cls.ModalHeader}>
                {!isAnswered && (
                    <Image
                        src="/images/new-year-poll.png"
                        className={cls.bannerImg}
                        width={578}
                        height={226}
                    />
                )}
                <Flex gap={12} direction="column" align={`${isAnswered ? 'center' : 'start'}`}>
                    {isAnswered && (
                        <span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="48"
                                height="32"
                                viewBox="0 0 48 32"
                                fill="none"
                            >
                                <path
                                    d="M15.5394 32C14.7459 32.0014 13.96 31.8499 13.2268 31.5541C12.4936 31.2584 11.8275 30.8242 11.2667 30.2766L0 19.2876L2.84841 16.507L14.1152 27.4979C14.4929 27.8663 15.0052 28.0733 15.5394 28.0733C16.0735 28.0733 16.5858 27.8663 16.9636 27.4979L45.1516 0L48 2.77868L19.812 30.2766C19.2513 30.8242 18.5852 31.2584 17.8519 31.5541C17.1187 31.8499 16.3328 32.0014 15.5394 32Z"
                                    fill="#28B57A"
                                />
                            </svg>
                        </span>
                    )}
                    <UiText align="left" tag="h4" variant="heading" weight="semibold">
                        {isAnswered
                            ? t('UserPoll.Popup.Content.h4')
                            : t('UserPoll.Modal.Header2.text.h4')}
                    </UiText>
                    {!isAnswered && (
                        <Controller
                            key="description"
                            name="userText"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    const plainText = getPlainTextFromHtml(value);
                                    if (!plainText) {
                                        return t('field.required');
                                    }
                                    if (plainText.length > 2000) {
                                        return t(
                                            'JournalEvents.EventJournalModal.Description.Error.Length'
                                        );
                                    }
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <TextEditor
                                    {...field}
                                    value={field.value ?? ''}
                                    inputName="text-editor"
                                    onChange={field.onChange}
                                    placeholder="Напишите ваши пожелания"
                                    readOnly={false}
                                    classes={cls.textEditorContainer}
                                    textEditorClassName={cls.textEditor}
                                    textEditorContentClassName={cls.textEditorContent}
                                />
                            )}
                        />
                    )}
                </Flex>
            </CustomModal.Header>
            {!isAnswered && (
                <CustomModal.Footer
                    classes={cn(cls.ModalFooter, isAnswered && cls.footerButtonLastChild)}
                >
                    {!isAnswered && (
                        <>
                            <UiButton
                                btnText={t('button.skip.text')}
                                variant="text"
                                onClick={handleSkip}
                                disabled={isMutating}
                                className={cn(cls.skipBtn, !skipBtn && cls.hidden)}
                            />

                            <UiButton
                                btnText={t('button.submit.text')}
                                onClick={handleSubmit}
                                disabled={isMutating || !isValid}
                            />
                        </>
                    )}
                </CustomModal.Footer>
            )}
        </CustomModal>
    );
};
